import AgoraRTC from 'agora-rtc-react';
import type { IAgoraRTCClient, IMicrophoneAudioTrack, ICameraVideoTrack } from 'agora-rtc-react';
import { StudioLogger } from '@studio/logger/StudioLogger';
import type { NO_PROXY_SERVER, UDP_PROXY_SERVER, TCP_PROXY_SERVER } from '@studio/constants/ReadinessCheck';
import { setupEventListeners } from '../events/setupEventListeners';
import {
  setupAudioDeviceListeners,
  setupVideoDeviceListeners,
  setupPlaybackDeviceListener
} from '../events/setupDeviceListeners';
import { NetworkHealthDispatcher } from '../metrics/NetworkHealthDispatcher';
import type { VideoInput } from '../video/videoHelpers';

const LOG = new StudioLogger('joinAgoraMeeting');

/* eslint-disable no-unused-vars */
interface Actions {
  showModal?: (...args: any[]) => any;
  showNotification: (...args: any[]) => any;
  clearNotification?: (...args: any[]) => any;
  handleVideoInputChanged: (...args: any[]) => any;
  handleAudioInputChanged: (...args: any[]) => any;
  setDesiredAttendeeCameraStatus?: (...args: any[]) => any;
  addToAttendeeTiles: (...args: any[]) => any;
  setAttendeeCameraStatus: (...args: any[]) => any;
  setSourceInitializing: (...args: any[]) => any;
  setAttendeeMuteStatus: (...args: any[]) => any;
  setAttendeeVoiceIntensity: (...args: any[]) => any;
  setAttendeePresence: (...args: any[]) => any;
  disconnectSource: (...args: any[]) => any;
  setAttendeeNetworkHealth?: (...args: any[]) => any;
  setSourceAsUnassigned?: (...args: any[]) => any;
  updateSource?: (...args: any[]) => any;
  removePresenter?: (...args: any[]) => any;
  setVideoInputDevice: (...args: any[]) => any;
}

interface JoinMeetingParams {
  channel: string;
  token: string;
  appId: string;
  audioInputDevice: string;
  attendeeId: string;
  actions: Actions;
  videoInput: VideoInput;
  dispatch: (action: any) => void;
  proxyType?: typeof NO_PROXY_SERVER | typeof UDP_PROXY_SERVER | typeof TCP_PROXY_SERVER;
}

/**
 * Joins an Agora RTC meeting with the specified parameters
 *
 * @param {JoinMeetingParams} params - The parameters for joining the meeting
 * @param {string} params.channel - The channel name to join
 * @param {string} params.token - The token for authentication
 * @param {string} params.appId - The Agora application ID
 * @param {string} params.audioInputDevice - The selected audio input device ID
 * @param {Object} params.videoInput - The selected video input
 * @param {string} params.attendeeId - The unique ID for the attendee joining the meeting
 * @param {Actions} params.actions - Actions that can be dispatched during the meeting
 * @param {Function} params.dispatch - Redux dispatch function
 *
 * @returns {Promise<{localAudioTrack: IMicrophoneAudioTrack, localVideoTrack: ICameraVideoTrack, client: IAgoraRTCClient}>}
 *          A promise that resolves to the created audio track, video track, and client
 */
export const joinMeeting = async ({
  channel,
  token,
  appId,
  audioInputDevice,
  attendeeId,
  actions,
  videoInput,
  dispatch,
  proxyType = 0
}: JoinMeetingParams): Promise<{
  localAudioTrack: IMicrophoneAudioTrack | null;
  localVideoTrack: ICameraVideoTrack | null;
  client: IAgoraRTCClient;
}> => {
  const client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp9' });

  // Default is 2 seconds which leads to some noticeable lag in volume indicator updates
  // @ts-ignore - setParameter exists in Agora SDK runtime but is not included in TypeScript definitions
  AgoraRTC.setParameter('AUDIO_VOLUME_INDICATION_INTERVAL', 200);

  setupEventListeners(client, dispatch, actions);

  NetworkHealthDispatcher.getInstance(client, attendeeId, dispatch, actions.setAttendeeNetworkHealth);

  LOG.info('Attempting to join Agora channel', {
    channel,
    attendeeId,
    appId,
    proxyType
  });

  if (proxyType > 0) {
    client.startProxyServer(proxyType);
    LOG.info(`Started Agora proxy server of type ${proxyType} for channel: ${channel}`);
  }

  await client.join(appId, channel, token, attendeeId);

  LOG.info('Successfully joined Agora channel', {
    channel,
    attendeeId
  });

  await client.enableDualStream();
  LOG.info(`Successfully dual stream mode enabled for Agora channel: ${channel}`);

  client.setLowStreamParameter({
    width: 320,
    height: 240,
    framerate: 15,
    bitrate: 400
  });

  const deviceActions = {
    handleVideoInputChanged: actions.handleVideoInputChanged,
    handleAudioInputChanged: actions.handleAudioInputChanged,
    showNotification: actions.showNotification,
    addToAttendeeTiles: actions.addToAttendeeTiles,
    setAttendeeCameraStatus: actions.setAttendeeCameraStatus,
    setVideoInputDevice: actions.setVideoInputDevice
  };

  const videoActions = {
    showNotification: actions.showNotification,
    addToAttendeeTiles: actions.addToAttendeeTiles,
    setAttendeeCameraStatus: actions.setAttendeeCameraStatus,
    setVideoInputDevice: actions.setVideoInputDevice
  };

  // This is only null for synthetics
  let localAudioTrack: IMicrophoneAudioTrack | null = null;
  try {
    localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({
      microphoneId: audioInputDevice,
      ANS: true,
      AEC: true
    });
    setupAudioDeviceListeners(localAudioTrack, dispatch, deviceActions);
  } catch (error: any) {
    LOG.error('Error creating microphone audio track', error);
  }

  setupVideoDeviceListeners(videoInput, dispatch, videoActions, attendeeId, client);

  setupPlaybackDeviceListener();

  const tracksToPublish = [localAudioTrack, videoInput.videoTrack].filter(
    (track): track is IMicrophoneAudioTrack | ICameraVideoTrack => Boolean(track)
  );

  if (tracksToPublish.length) {
    await client.publish(tracksToPublish);
  }

  LOG.info(`Successfully joined Agora channel: ${channel} as ${attendeeId} with proxyType: ${proxyType}`);

  const localVideoTrack = videoInput.videoTrack;
  return {
    localAudioTrack,
    localVideoTrack,
    client
  };
};
